/**
 * Zone 3: Sky Realm Decorator
 *
 * Handles decoration for the Sky Realm zone (Zone 3)
 * Includes 7 decoration types: 5 top-side + 2 underside
 *
 * Top decorations: cloud-puff, flying-bird, sky-butterfly, floating-feather, wind-swirl
 * Underside decorations: hanging-liana, hanging-leaves-dense
 *
 * PHASE 7C: Converted from Graphics to Sprites
 */

import Phaser from 'phaser';
import { PlatformDecoratorBase, DecorationConfig } from './PlatformDecoratorBase';
import { DecorationTextureGenerator } from '../DecorationTextureGenerator'; // PHASE 7C

export class Zone3Decorator extends PlatformDecoratorBase {
  protected getDecorations(): DecorationConfig[] {
    return [
      // Top-side decorations
      {
        type: 'cloud-puff',
        chance: 0.2,
        minWidth: 60,
        placement: 'top',
      },
      {
        type: 'flying-bird',
        chance: 0.15,
        minWidth: 50,
        placement: 'top',
      },
      {
        type: 'sky-butterfly',
        chance: 0.12,
        minWidth: 45,
        placement: 'top',
      },
      {
        type: 'floating-feather',
        chance: 0.18,
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'wind-swirl',
        chance: 0.14,
        minWidth: 55,
        placement: 'top',
      },
      // Underside decorations
      {
        type: 'hanging-liana',
        chance: 0.5,
        minWidth: 70,
        placement: 'underside',
      },
      {
        type: 'hanging-leaves-dense',
        chance: 0.45,
        minWidth: 50,
        placement: 'underside',
      },
    ];
  }

  /**
   * Add a specific decoration to the platform
   * PHASE 7C: CONVERTED TO SPRITES (was Graphics)
   */
  protected addDecoration(
    platformContainer: Phaser.GameObjects.Container,
    decorationType: string,
    _placement: 'top' | 'underside',
    width: number,
    height: number
  ): void {
    // PHASE 7C: Create sprite from pre-generated texture
    const textureKey = DecorationTextureGenerator.getTextureKey(decorationType);
    const sprite = this.scene.add.sprite(0, 0, textureKey);
    sprite.setOrigin(0.5, 0.5);
    platformContainer.add(sprite);

    const position = this.calculateDecorationPosition(decorationType, width, height);
    sprite.setPosition(position.x, position.y);

    const animationType = this.getAnimationType(decorationType);
    if (animationType) {
      this.animatedElements.push({
        sprite,
        type: animationType,
        animationPhase: this.random.nextFloat(0, Math.PI * 2),
        animationSpeed: this.getAnimationSpeed(animationType),
      });
    }
  }

  private calculateDecorationPosition(
    type: string,
    width: number,
    height: number
  ): { x: number; y: number } {
    // CRITICAL FIX: Only use platform-dependent positioning (constant offsets in texture)
    switch (type) {
      case 'cloud-puff':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the random -10 to -20 offset
        };
      case 'flying-bird':
        return {
          x: this.random.nextFloat(-width * 0.35, width * 0.35),
          y: -height / 2, // Texture has the random -15 to -30 offset
        };
      case 'sky-butterfly':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the random -15 to -25 offset
        };
      case 'floating-feather':
        return {
          x: this.random.nextFloat(-width * 0.4, width * 0.4),
          y: -height / 2, // Texture has the random -12 to -25 offset
        };
      case 'wind-swirl':
        return {
          x: this.random.nextFloat(-width * 0.2, width * 0.2),
          y: -height / 2, // Texture has the random -8 to -15 offset
        };
      case 'hanging-liana':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: height / 2,
        };
      case 'hanging-leaves-dense':
        return {
          x: this.random.nextFloat(-width * 0.2, width * 0.2),
          y: height / 2,
        };
      default:
        return { x: 0, y: 0 };
    }
  }

  private getAnimationType(type: string): string | null {
    switch (type) {
      case 'floating-feather':
        return 'feather-float';
      default:
        return null;
    }
  }

  private getAnimationSpeed(type: string): number {
    switch (type) {
      case 'feather-float':
        return 0.05;
      default:
        return 0.05;
    }
  }
}
